import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { customInstanceDocumentos } from "../../../api/axios-client";
import { useAuth } from "../../../auth";
import ListLayout from "../../../components/Layout/ListLayout";
import { AnalysisList } from "./components/AnalysisList";
import { AnalysisCards } from "./components/AnalysisCards";
import { AnalysisFilters } from "./components/AnalysisFilters";
import { useMediaQuery, useTheme, TablePagination } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const analysisSearchSchema = z.object({
    page: z.number().default(1),
    size: z.number().default(10),
    sort: z.string().default('id:desc'),
    bySituation: z.number().default(2),
    q: z.string().optional(),
    month: z.string().optional(),
    byRequester: z.string().optional(),
});

export const Route = createFileRoute('/__auth/documents/analysis')({
    component: AnalysisPage,
    staticData: {
        title: "routes.documentAnalysis",
    },
    validateSearch: analysisSearchSchema,
});

function AnalysisPage() {
    const { user } = useAuth();
    const search = Route.useSearch();
    const navigate = useNavigate({ from: Route.fullPath });
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const monthYearParams = useMemo(() => {
        if (search.month) {
            const [year, month] = search.month.split('-');
            return { year: parseInt(year), month: parseInt(month) };
        }
        return {};
    }, [search.month]);

    const queryParams = useMemo(() => {
        const params = new URLSearchParams();
        params.set('page', String((search.page || 1) - 1));
        params.set('size', String(search.size || 10));
        params.set('isPageable', 'true');
        params.set('sort', (search.sort || 'id:desc').replace(':', ','));
        if (search.bySituation !== undefined) params.set('bySituation', String(search.bySituation));
        if (search.q) params.set('q', search.q);
        if (search.byRequester) params.set('byRequester', search.byRequester);
        if (monthYearParams.year) params.set('year', String(monthYearParams.year));
        if (monthYearParams.month) params.set('month', String(monthYearParams.month));
        return params.toString();
    }, [search, monthYearParams]);

    const tenantId = user?.tenant ?? "";

    const { data: dataBlob, isLoading, error } = useQuery({
        queryKey: ['pending-documents', tenantId, queryParams],
        queryFn: () => customInstanceDocumentos<any>(
            `/${tenantId}/pending-documents?${queryParams}`,
            { method: 'GET' }
        ),
        enabled: !!tenantId,
    });

    const data = (dataBlob?.data ?? dataBlob) as any;

    const handlePageChange = (_: unknown, newPage: number) => {
        navigate({ search: (prev) => ({ ...prev, page: newPage + 1 }) });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        navigate({ search: (prev) => ({ ...prev, size: parseInt(event.target.value, 10), page: 1 }) });
    };

    const handleFilterChange = (newFilters: any) => {
        navigate({ search: (prev) => ({ ...prev, ...newFilters, page: 1 }) });
    };

    const handleClearFilters = () => {
        navigate({ search: { page: 1, size: 10, sort: 'id:desc', bySituation: 2 } });
    };

    return (
        <ListLayout
            title={t("documents.documentAnalysis")}
            subtitle={t("documents.analysis.subtitle")}
            isLoading={isLoading}
            error={error}
            isEmpty={!data?.content?.length}
            filters={
                <AnalysisFilters
                    filters={search}
                    onChange={handleFilterChange}
                    onClear={handleClearFilters}
                />
            }
        >
            {isMobile ? (
                <AnalysisCards data={data?.content ?? []} searchParams={search} />
            ) : (
                <AnalysisList data={data?.content ?? []} searchParams={search} />
            )}
            <TablePagination
                component="div"
                count={data?.totalElements ?? 0}
                page={(search.page || 1) - 1}
                onPageChange={handlePageChange}
                rowsPerPage={search.size || 10}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t("common.itemsPerPage")}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
        </ListLayout>
    );
}
